import { ActorRefFrom, InterpreterFrom, StateMachine } from "xstate"
import { FarePriceResponse, PricesResponseV2, SuitePayment, SuitePrice } from "@ssc-booking/api-contracts"
import { useActor } from "@xstate/react"
import { Ev } from "../../../_common/machines/event"
import { ECommerceEvent, ECommerceState } from "./ecommerce-machine/e-commerce-machine"
import { FareCardRef } from "./ecommerce-machine/e-commerce-machine-context"

export type SuitesPricesResponse = PricesResponseV2["prices"]

export type SuitePriceFrom = {
    type: "price-from"
    price: SuitePrice
}

export type SuitePriceTotal = {
    type: "price-total"
    price: SuitePrice
    suitePayment: SuitePayment
}

export type Receipt = SuitePriceFrom | SuitePriceTotal

type InitialReceiptContext = {
    farePrices: Record<number, FarePriceResponse>
    suitePrices: SuitesPricesResponse
    selectedFare?: FareCardRef
    selectedSuiteCategory?: string
}

type PresentContext = InitialReceiptContext & {
    receipt: Receipt
}
type PresentPriceFromContext = PresentContext & {
    receipt: SuitePriceFrom
}
type PresentPriceTotalContext = PresentContext & {
    receipt: SuitePriceTotal
}

export type ReceiptTypeState =
    | { value: "empty"; context: InitialReceiptContext }
    | { value: "present"; context: PresentContext }
    | { value: "present.priceFrom"; context: PresentPriceFromContext }
    | { value: "present.priceTotal"; context: PresentPriceTotalContext }
    | { value: "loading"; context: PresentContext }

export type ReceiptContext = InitialReceiptContext & { receipt?: Receipt }
export type ReceiptEvents = ECommerceEvent | Ev<"FARE_RECEIPT_AVAILABLE"> | Ev<"SUITE_CATEGORY_RECEIPT_AVAILABLE">

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReceiptStateMachine = StateMachine<ReceiptContext, any, ReceiptEvents, ReceiptTypeState>
type ReceiptInterpreter = InterpreterFrom<ReceiptStateMachine>
export type ReceiptState = ReceiptInterpreter["state"]
export type ReceiptSend = ReceiptInterpreter["send"]
type ReceiptActorRef = ActorRefFrom<ReceiptStateMachine>
export type ReceiptTypes = {
    Context: ReceiptContext
    Events: ReceiptEvents
    TypeState: ReceiptTypeState
    StateMachine: ReceiptStateMachine
    Interpreter: ReceiptInterpreter
    State: ReceiptState
    Send: ReceiptSend
    ActorRef: ReceiptActorRef
}

export const useReceiptReadModel = (state: ECommerceState): [ReceiptState, ReceiptSend] => {
    const receiptMachine = state.children.receiptReadModel as ReceiptActorRef
    return useActor(receiptMachine) as unknown as [ReceiptState, ReceiptSend]
}
